import React, { useMemo } from "react";
import "./Financial.scss";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Container } from "@mui/material";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import CustomizedDialogs from "./Dialog.jsx";
import {
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  MotifIcon
} from "@ey-xd/motif-react";
import { infoIcon} from '../../../common/MotifIcons';
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-datalabels";
import FilterButton from "./BenchmarkFilters.jsx";
import { getLabelKey, labelMap, mapKpi } from "./labelMap.js";
Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartDataLabels,
  Title,
  Tooltip,
  Legend
);

export const Financial = ({ sectorData, sendDataToParent , isLoading}) => {
  const [value, setValue] = React.useState("1");
  const [avg, setAvg] = React.useState(3);
  const [selectedLabel, setSelectedLabel] = React.useState("");
  const sheetData = useMemo(() => {
    if (value == 1 && sectorData) {
      return sectorData["Financial"]?.profitAndLosses;
    } else {
      return sectorData["Financial"]?.balanceSheets;
    }
  }, [sectorData, value, avg]);
  const filters = useMemo(() => {
    return [
      {
        title: "KPI",
        items: mapKpi(sheetData, value),
      },
    ];
  }, [value, sectorData, avg]);
  const [pageFilters, setpageFilter] = React.useState({});
  const handlePageFilter = (val) => {
    if (!val) {
      setpageFilter({});
    } else {
      setpageFilter(val);
    }
  };
  const graphData = useMemo(() => {
    const selectedFilters = Object.keys(pageFilters["KPI"] ?? {})?.filter(
      (key) =>
        pageFilters["KPI"]?.[key] &&
        sheetData?.map((val) => val.func_kpi).indexOf(key) != -1
    );
    const selectedData = sheetData?.filter(
      (val) => selectedFilters.indexOf(val.func_kpi) != -1
    );

    const labels = selectedFilters?.map((val) => labelMap[val]);
    return {
      labels,
      datasets: [
        {
          label: "Average Industry Value",
          data: selectedData?.map((val) => Number(val.avgIndustryValue).toFixed(2)),
          type: "bar",
          backgroundColor: "#188CE5",
          barThickness: 20,
        },
        {
          label: avg == 3 ? "Average of top 3 peers" : "Avg of top 5 peers",
          data: selectedData?.map((val) => {
            if (value === "2") {
              return Number(val.avgTopValue / 100).toFixed(2);
            } else {
              return Number(val.avgTopValue).toFixed(2);
            }
          }),
          type: "bar",
          backgroundColor: "#0A558E",
          barThickness: 20,
        },
      ],
    };
  }, [pageFilters, filters, value]);

  const [lineGraphDataset, setlineGraphDataset] = React.useState({});
  const [openClose, setOpenClose] = React.useState(false);

  const handleChangeSelect = (event) => {
    setAvg(event.target.value);
    sendDataToParent(event.target.value);
  };
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let maxDataValue = 0;
  const NWCChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      datalabels: {
        color: "#000",
        align: "top", // Align label to the end of the bar
        anchor: "end", // Anchor label to the end of the bar
        padding: 4, // Add some padding to move the label slightly away from the bar end
        font: {
          weight: "bold",
          size: 12,
        },
        formatter: (value, context) => {
          return value; // Append a '%' sign to the label
        },
        display: true, // Enable the display of datalabels
      },
      legend: {
        align: "end",
        labels: {
          font: {
            size: 8,
            align: "right",
          },
          usePointStyle: true,
       
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          dash: [5, 5],
        },
      },
      y: {
        grid: {
          display: true,
        },
        border: {
          dash: [2, 4],
        },
      },
    },
    onClick: (event, elements) => {
      if (elements && elements.length > 0) {
        const chartElement = elements[0];
        const index = chartElement["index"];
        const label = graphData.labels[index];
        const value = graphData.datasets[0].data[index];
        sendDataToGraph(label);
      }
    },
  };
  const sendDataToGraph = (ele) => {
    setSelectedLabel(ele);
    const searchElement = value == 1 ? getLabelKey(ele) : ele;
    const resultData = sheetData?.find(
      (labelValue) => labelValue.func_kpi == searchElement
    );
    const lastThreeYear = getDataBasedOnMonth(resultData["lastthreeYears"]);
    const lastYear = getDataBasedOnMonth(resultData["lastYears"]);
    const lastSixMonth = getDataBasedOnMonth(resultData["lastYears"])?.slice(
      0,
      6
    );
    setlineGraphDataset({
      lastThreeYear: lastThreeYear,
      lastYear: lastYear,
      lastSixMonth: lastSixMonth,
    });
    setOpenClose(true); // set the data from the child to the state
  };
  const getDataBasedOnMonth = (data) => {
    const monthOrder = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };
    if (data) {
      return [...data]?.sort((a, b) => {
        return (
          monthOrder[a.reported_month_name] - monthOrder[b.reported_month_name]
        );
      });
    } else {
      return [];
    }
  };

  const handleGraph = (elements) => {
    setOpenClose(false);
  };
  const sendData = () => {
    const data = "Data from child";
    sendDataToParent(data);
  };
  const pageFiltersData = useMemo(() => {
    const selectedFilters = Object.keys(pageFilters["KPI"] ?? {}).filter(
      (key) =>
        pageFilters["KPI"]?.[key] &&
        sheetData?.some((val) => val.func_kpi === key)
    );
    if (selectedFilters && selectedFilters.length == 0) {
      return true;
    } else if (value === 1) {
      return !sheetData.some((val) =>
        selectedFilters.includes(labelMap[val.func_kpi])
      );
    } else if (value === 2) {
      return !sheetData.some((val) =>
        selectedFilters.includes(labelMap[val.func_kpi])
      );
    }
  }, [value, sectorData, avg, pageFilters]);
  return (
    <div style={{ width: "100%" }} className="financials">
      <Container maxWidth="xl">
        <div
          style={{
            border: "1px solid #e0e0e0",
            padding: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                textAlign: "left",

                fontSize: "20px",
                color: "black",
                fontWeight: "600",
              }}
            >
              Select the KPIs and enter their values
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FilterButton
                sectionsData={filters}
                handlePageFilter={handlePageFilter}
                currentTab={value}
              ></FilterButton>

              {!pageFiltersData ? (
                <div
                  className="filter-dropdown"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <div className="v-align">
                    <select value={avg} onChange={handleChangeSelect}>
                      <option value={3}>Average of top 3 peers</option>
                      <option value={5}>Average of top 5 peers</option>
                    </select>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
            
            <TabContext value={value}>
            <TabList onChange={handleChange} aria-label="lab API tabs example">
              <Tab label="Profit & Loss" value="1" />
              <Tab label="Balance Sheet" value="2" />
              {/* <Tab label="Working Capital" value="3" /> */}
            </TabList>
            <TabPanel value="1" className="tab-panel">
              {
             isLoading  ? "Loading" : sheetData?.length > 0  ?
                <Bar
                  style={{ width: "80%", height: "50%" }}
                  data={graphData}
                  options={{
                    ...NWCChartOptions,
                    plugins: {
                      datalabels: {
                        color: "#000",
                        align: "top", // Align label to the end of the bar
                        anchor: "end", // Anchor label to the end of the bar
                        padding: 4, // Add some padding to move the label slightly away from the bar end
                        font: {
                          weight: "bold",
                          size: 12,
                        },
                        formatter: (value, context) => {
                          return value + "%"; // Append a '%' sign to the label
                        },
                        display: true, // Enable the display of datalabels
                      },
                    },
                  }}
                /> : "No data found"
              }
            </TabPanel>
            <TabPanel value="2" className="tab-panel">
              {
                  isLoading  ? "Loading" : sheetData?.length > 0  ?
                <Bar
                  style={{ width: "80%", height: "50%" }}
                  data={graphData}
                  options={NWCChartOptions}
                />  : "No data found"
              }
            </TabPanel>
            <TabPanel value="3"></TabPanel>
          </TabContext> 
        </div>
         <div className='warning-msg'> <MotifIcon src={infoIcon} /><div className='text'> Outputs are generated by AI. AI can generate incorrect predictions. Review of all generated content is strongly advised.</div></div>
      </Container>

      <CustomizedDialogs
        dialogTitle={value === "1" ? selectedLabel + " %" : selectedLabel}
        openClose={openClose}
        handleOpenClose={handleGraph}
        lineGraphDataset={lineGraphDataset}
        value={value}
      ></CustomizedDialogs>
    </div>
  );
};
